import { Alert, Divider, Group } from '@mantine/core';
import { useApiMemberEnv } from '@repo/api-codegen';
import { OPERATORS_CONFIG } from '@repo/common/components/Filter/constants';
import {
	FilterOperator,
	type FilterValue,
} from '@repo/common/components/Filter/types';
import { UserRole } from '@repo/common/enums/UserRole';
import { LineageFlow, LineageFlowProvider } from '@repo/lineage';
import {
	useLineageFlowContext,
	useLineageFlowFilterContext,
} from '@repo/lineage/context/LineageFlowProvider/LineageFlowContext';
import type LineageFlowStore from '@repo/lineage/store/LineageFlowStore';
import type LineageFlowUIStore from '@repo/lineage/store/LineageFlowUIStore';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import type { SearchFilterV2Store } from '../Filter';
import { AddFilter, Filter, FilterOptionType } from '../Filter';
import {
	FILTER_OPTIONS_CONFIG,
	getLineageDepthItems,
} from '../Filter/constants';
import { ImpactAnalysisModal } from './Modals';

import { useSuspenseFeatureAccessCheck } from '../../utils/authorization/accessMessages';
import { FeatureGuard } from '../FeatureGuard/FeatureGuard';

const LineageFilters = observer(() => {
	const { upstreamDepth, downstreamDepth, onSetDepth } =
		useLineageFlowContext();
	const { filterOptions, values, onAddValue, onChangeValue, onClearValue } =
		useLineageFlowFilterContext();

	// Get dynamic depth items based on workspace lineage availability
	const { data: memberEnv } = useApiMemberEnv({});
	const maxDepth = memberEnv?.MAX_LINEAGE_DEPTH;
	const workspaceLineageAvailable =
		memberEnv?.WORKSPACE_LINEAGE_GENERATED ?? true;

	// Memoize handlers to prevent recreating functions on each render
	const handleUpstreamDepthChange = useCallback(
		(value: Partial<FilterValue>) => {
			if (value.value !== undefined && typeof value.value === 'number') {
				onSetDepth({
					direction: 'UPSTREAM',
					depth: value.value,
				});
			}
		},
		[onSetDepth]
	);

	const handleDownstreamDepthChange = useCallback(
		(value: Partial<FilterValue>) => {
			if (value.value !== undefined && typeof value.value === 'number') {
				onSetDepth({
					direction: 'DOWNSTREAM',
					depth: value.value,
				});
			}
		},
		[onSetDepth]
	);

	// Memoize operator config objects
	const singleOperatorConfig = useMemo(
		() => ({
			getOperators: () => [FilterOperator.Is],
			getLabel: (operator: FilterOperator) => operator.toString(),
		}),
		[]
	);

	// Get dynamic depth items based on workspace lineage availability
	const depthItems = useMemo(() => getLineageDepthItems(maxDepth), [maxDepth]);

	// Create dynamic filter options with correct depth items
	const upstreamDepthFilterOption = useMemo(
		() => ({
			...FILTER_OPTIONS_CONFIG[FilterOptionType.UPSTREAM_DEPTH],
			filterDropdownConfig: {
				...FILTER_OPTIONS_CONFIG[FilterOptionType.UPSTREAM_DEPTH]
					.filterDropdownConfig,
				getItems: depthItems,
			},
		}),
		[depthItems]
	);

	const downstreamDepthFilterOption = useMemo(
		() => ({
			...FILTER_OPTIONS_CONFIG[FilterOptionType.DOWNSTREAM_DEPTH],
			filterDropdownConfig: {
				...FILTER_OPTIONS_CONFIG[FilterOptionType.DOWNSTREAM_DEPTH]
					.filterDropdownConfig,
				getItems: depthItems,
			},
		}),
		[depthItems]
	);

	return (
		<Group spacing="xs" align="flex-start" style={{ flexWrap: 'wrap' }}>
			{!workspaceLineageAvailable && (
				<Alert color="yellow" w="100%" mb="xs">
					Workspace lineage graphs are being generated. Advanced lineage
					features (depth &gt; 1) will be available once generation completes.
				</Alert>
			)}
			<Filter
				value={{
					filterType: FilterOptionType.UPSTREAM_DEPTH,
					value: upstreamDepth,
					operator: FilterOperator.Is,
				}}
				filterOption={upstreamDepthFilterOption}
				onChange={handleUpstreamDepthChange}
				showDetailedLabel
				operatorConfig={singleOperatorConfig}
			/>
			<Filter
				value={{
					filterType: FilterOptionType.DOWNSTREAM_DEPTH,
					value: downstreamDepth,
					operator: FilterOperator.Is,
				}}
				filterOption={downstreamDepthFilterOption}
				onChange={handleDownstreamDepthChange}
				showDetailedLabel
				operatorConfig={singleOperatorConfig}
			/>
			<Divider orientation="vertical" />
			{values.map((value, idx) => (
				<Filter
					key={`${value.filterType}-${idx}`}
					value={toJS(value)}
					filterOption={FILTER_OPTIONS_CONFIG[value.filterType]}
					onChange={onChangeValue(idx)}
					onClear={onClearValue(idx)}
					showDetailedLabel
					operatorConfig={
						OPERATORS_CONFIG[
							FILTER_OPTIONS_CONFIG[value.filterType].filterDropdownConfig
								.dropdownType
						]
					}
				/>
			))}
			<AddFilter options={toJS(filterOptions)} onAddFilter={onAddValue} />
		</Group>
	);
});

const LineageTabInternal = observer(
	({
		filters,
		createAccess,
	}: {
		filters: ReactNode;
		createAccess: boolean;
	}) => {
		const { catalogFilter } = useLineageFlowFilterContext();
		const memoizedCatalogFilter = useMemo(
			() => toJS(catalogFilter),
			[catalogFilter]
		);
		return (
			<LineageFlow
				catalogFilter={memoizedCatalogFilter}
				filters={filters}
				createAccess={createAccess}
				showActionBar
			/>
		);
	}
);

interface ILineageTabProps {
	lineageFlowStore: LineageFlowStore;
	lineageFlowUIStore: LineageFlowUIStore;
	lineageFilterStore: SearchFilterV2Store;
}

export const LineageTab = observer(
	({
		lineageFlowStore,
		lineageFlowUIStore,
		lineageFilterStore,
	}: ILineageTabProps) => {
		const { restrictionMessage } = useSuspenseFeatureAccessCheck({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Lineage.Create',
			isFeatureIncludedInPlan: true,
			isAlwaysIncludedFeature: true,
		});

		return (
			<FeatureGuard
				featureName="Lineage"
				v1AllowedRoles={[
					UserRole.VIEWER,
					UserRole.GUEST,
					UserRole.ADMIN,
					UserRole.EDITOR,
				]}
				v2Permission="Lineage.Read"
				isFeatureIncludedInPlan={true}
				isAlwaysIncludedFeature={true}
			>
				<LineageFlowProvider
					filterStore={lineageFilterStore}
					lineageFlowStore={lineageFlowStore}
					lineageFlowUIStore={lineageFlowUIStore}
				>
					<LineageTabInternal
						filters={<LineageFilters />}
						createAccess={!restrictionMessage}
					/>
					<ImpactAnalysisModal />
				</LineageFlowProvider>
			</FeatureGuard>
		);
	}
);
